import { Inject, Injectable, InjectionToken, OnDestroy } from '@angular/core';
import { CbreAlertService } from '@cbre/alerts';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';
import { DataObject } from '@shared/models/data-object.model';
import { SharedService } from '@shared/services/shared.service';
import { L10nTranslationService } from 'angular-l10n';
import deepmerge from 'deepmerge';
import { concat, EMPTY, Observable, of } from 'rxjs';
import { catchError, tap, toArray } from 'rxjs/operators';
import { RecordSaver } from '../models/record-saver';
import { MethodErrorHandlerFactoryService } from './errors/handlers/method-error-handler-factory.service';
import { FullDigestedActivityParams } from './update-record.model';
import { v4 as uuidv4 } from 'uuid';
import { EntityType } from '@miq/domain';

export const AliasedUpdateRecordService = new InjectionToken<UpdateRecordService>('AliasedRecordUpdate');

const TRACKING_CONFIGM_MAPPING: { [key in EntityType]?: { enabled: boolean } } = {
    [EntityType.Availability]: { enabled: true },
    [EntityType.Property]: { enabled: false },
    [EntityType.LeaseComp]: { enabled: false },
    [EntityType.Tenancy]: { enabled: false },
    [EntityType.SaleComp]: { enabled: false },
    [EntityType.Tim]: { enabled: false },
    [EntityType.Company]: { enabled: false },
    [EntityType.Contact]: { enabled: false },
  }

@Injectable({
  providedIn: 'root',
})
export class UpdateRecordService implements RecordSaver, OnDestroy {
  private digestedRequests = { h: [] };

  /**
   * Concat edp requests and map failure responses into common object.
   * @param requests EDP requests.
   */

  constructor(
    private sharedService: SharedService,
    private alertsService: CbreAlertService,
    private translationService: L10nTranslationService,
    private methodErrorHandlerFactoryService: MethodErrorHandlerFactoryService,

    @Inject(CbreLoggerToken) private logger: CbreLogger
  ) {}

  ngOnDestroy(): void {
    this.digestedRequests = { h: [] };
  }

  public save(): Observable<any> {
    const requestTuples = this.makeRequests();
    const request$ = requestTuples.map(([, request]) => request);
    return concat(...request$).pipe(
      toArray(),
      catchError(obj =>  of({ failed: obj ? obj.id : obj }))
    );
  }

  private makeRequests(): Array<[string, Observable<DataObject>]> {
    // Count the total number of payloads to be generated for tracking purposes
    const totalCount = this.digestedEntries.length;
    const actionGroupKey = uuidv4();
    
    //const action_group_key =  
    return this.digestedEntries.map(([taskKey, data]) => {
      const [entity, recordId, method] = taskKey.split('.');
      // requestMethod (GET/POST) permits specifying which http method to use for specific payload
      const requestMethod = data.requestMethod;

      // Inject tracking metadata if enabled for this entity
      if (TRACKING_CONFIGM_MAPPING[entity as EntityType]?.enabled) {
        data._meta = {
          action_group_key: actionGroupKey,
          action_count: totalCount,
        };
      }
      return [
        taskKey,
        this.sharedService.updateRecordOnEDP(entity, recordId, data, method, requestMethod).pipe(
          tap(() => {
            this.deleteDigestedActivity(taskKey);
            this.alertsService.showSuccessSnackBar_l10n('recordsSearch.updatedFields');
          }),
          catchError(error => {
            this.logger.error(error);
            this.showError(method, data, error);
            this.deleteDigestedActivity(taskKey);
            return EMPTY;
          })
        ),
      ];
    });
  }

  private get digestedEntries(): Array<[string, DataObject]> {
    return this.digestedRequests.h.map(key => [key, this.digestedRequests[key]]);
  }

  /**
   * Components that needs to generate payload
   * @param recordId
   * @param param1
   * @returns
   */
  public addDigestedActivity({ recordId, entity, identifier, data, method }: FullDigestedActivityParams) {
    const actionKey = `${entity}.${recordId}.${method}.${identifier}`;
    if (!this.digestedRequests.h.some(x => x === actionKey)) {
      this.digestedRequests.h = [...this.digestedRequests.h, actionKey];
    }
    const current = this.digestedRequests[actionKey] || {};
    const combineMerge = (target, source, options) => {
      const destination = target.slice();
      source.forEach((item, index) => {
        if (typeof destination[index] === 'undefined') {
          destination[index] = options.cloneUnlessOtherwiseSpecified(item, options);
        } else if (options.isMergeableObject(item)) {
          destination[index] = deepmerge(target[index], item, options);
        } else if (target.indexOf(item) === -1) {
          destination.push(item);
        }
      });
      return destination;
    };
    this.digestedRequests[actionKey] = deepmerge(current, data, { arrayMerge: combineMerge });
    return this.digestedRequests[actionKey];
  }

  public deleteDigestedActivity(fullTaskId: string) {
    delete this.digestedRequests[fullTaskId];
    this.digestedRequests.h = this.digestedRequests.h.filter(actionId => actionId !== fullTaskId);
  }

  public deleteDigestedActivityWithParams({ entity, recordId, method, identifier }: FullDigestedActivityParams) {
    this.deleteDigestedActivity(`${entity}.${recordId}.${method}.${identifier}`);
  }

  public clearDigestedActivity() {
    this.digestedRequests = { h: [] };
  }

  /**
   * To display snackbar error when edp method failed with a given payload.
   * If known translation use translation else display generic message
   * @param failingMethodName
   * @param failingPayload
   */
  private showError(failingMethodName: string, failingPayload: DataObject = {}, errorResponse: any): void {
    const requestHandleInstance = this.methodErrorHandlerFactoryService.getHandlerError(failingMethodName);

    if (requestHandleInstance) {
      requestHandleInstance.handleError(failingPayload, errorResponse);
    } else {
      const knownMessage = `detailsErrors.error.edpResponse.${failingMethodName}`;

      if (this.translationService.has(knownMessage)) {
        // show specific message built out of the method name
        this.alertsService.showErrorSnackBar_l10n({ message: knownMessage, params: failingPayload });
      } else {
        // show generic error message
        this.alertsService.showErrorSnackBar_l10n('recordsSearch.failedUpdatingFields');
      }
    }
  }
}
